import { Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ApiError, type NodeInfo } from '../../api'
import { NODES_KEY, fetchNodes } from '../Nodes'
import { RELAY, getAsOf, relayKeys, routePath, POLL_MS, type NoPathStream, type RelayJournalEvent, type RelayList, type RelayRoute } from './api'
import { EventList } from './events'
import { utcClock, useOperatorEvents } from './operatorEvents'
import { Icon } from '../../ui'
import { ago, ms } from './format'
import { noteRelayAsOf, noteRelayFailure, type RelayLive } from './live'
import { PathLine } from './path'
import { errorText } from './queries'
import { HOP_SPEC, NO_PATH_TEXT, ROUTE_SPEC, reasonText } from './states'
import { EpochTag, ErrorBox, NetPanel, SkeletonRows, StateBadge, StateText } from './ui'

/*
 * Node card › Streams and Events (proto nodecard/Tables.tsx).
 * Streams: the live routes whose path has the node (OBS here = ingress; legs
 * that leave here = egress / hub), plus the streams of this node that have no
 * relay path (NodeRelay.no_path). Each links to its route card.
 * Events: the relay journal of the node (GET /relay/journal?node=) and the
 * journal events of the stream that name it, newest first.
 */

/** Node names, roles and status by id (GET /api/admin/nodes). */
export function useNames() {
  const q = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes, refetchInterval: 15_000 })
  const m = new Map((q.data ?? []).map((n) => [n.id, n]))
  return { nameOf: (id?: string) => (id ? (m.get(id)?.name ?? id) : '—'), rolesOf: (id: string) => m.get(id)?.roles, offline: (id: string) => !!m.get(id) && m.get(id)!.status !== 'online' }
}

function HopHere({ r, nodeId }: { r: RelayRoute; nodeId: string }) {
  const inn = r.hops.find((h) => h.to.id === nodeId)
  const out = r.hops.find((h) => h.from.id === nodeId)
  const cell = (h: RelayRoute['hops'][number] | undefined, dir: string) =>
    !h ? null : h.state === 'up' ? (
      <span className="num text-xs whitespace-nowrap text-text-soft">
        {dir} {ms(h.lat_p50_ms)}
        <span className="text-muted"> · p99 {ms(h.lat_p99_ms)}</span>
      </span>
    ) : (
      <span className="text-xs whitespace-nowrap">
        {dir} <StateText spec={HOP_SPEC[h.state]} />
      </span>
    )
  return (
    <span className="flex flex-col gap-0.5">
      {cell(inn, 'in')}
      {cell(out, 'out')}
      {!inn && !out && <span className="text-faint">—</span>}
    </span>
  )
}

function RouteRows({ rows, n, label, testId }: { rows: RelayRoute[]; n: NodeInfo; label: string; testId: string }) {
  const names = useNames()
  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[760px] text-left text-sm" aria-label={label} data-testid={testId}>
          <thead className="border-b border-line text-xs text-muted">
            <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium [&>th:first-child]:pl-4">
              <th scope="col">Stream</th>
              <th scope="col">Route</th>
              <th scope="col">Path</th>
              <th scope="col">Hop here</th>
              <th scope="col">Outputs</th>
              <th scope="col">State</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((r) => (
              <tr key={r.route_id} data-testid={`node-route-${r.route_id}`} data-state={r.state} className="[&>td]:px-3 [&>td]:py-2 [&>td]:align-top [&>td:first-child]:pl-4">
                <td>
                  <span className="font-mono text-xs font-semibold">{r.stream}</span>
                  {r.user?.email && <div className="truncate text-[11px] text-muted">{r.user.email}</div>}
                </td>
                <td>
                  <Link to={routePath(r.route_id)} className="font-mono text-xs underline-offset-2 hover:underline">
                    {r.route_id}
                  </Link>
                  <div className="mt-0.5">
                    <EpochTag e={r.epoch} />
                  </div>
                </td>
                <td>
                  <PathLine path={r.path} hops={r.hops} rolesOf={names.rolesOf} offline={names.offline} />
                </td>
                <td>
                  <HopHere r={r} nodeId={n.id} />
                </td>
                <td className="num text-xs whitespace-nowrap text-text-soft">
                  {r.outputs.by_state.live ?? 0}/{r.outputs.total} live
                </td>
                <td>
                  <StateBadge spec={ROUTE_SPEC[r.state]} />
                  {r.state_reason && <div className="mt-1 max-w-[220px] text-[11px] text-muted">{reasonText(r.state_reason)}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="divide-y divide-line md:hidden" aria-label={label}>
        {rows.map((r) => (
          <li key={r.route_id} data-testid={`node-route-card-${r.route_id}`}>
            <Link to={routePath(r.route_id)} className="flex min-h-[44px] flex-col gap-1.5 px-4 py-3">
              <span className="flex items-start justify-between gap-2">
                <span className="font-mono text-sm font-semibold">{r.stream}</span>
                <StateBadge spec={ROUTE_SPEC[r.state]} />
              </span>
              <span className="flex flex-wrap items-center gap-2 text-xs text-muted">
                <span className="font-mono">{r.route_id}</span>
                <EpochTag e={r.epoch} />
                <span className="num">
                  {r.outputs.by_state.live ?? 0}/{r.outputs.total} live
                </span>
              </span>
              <HopHere r={r} nodeId={n.id} />
            </Link>
          </li>
        ))}
      </ul>
    </>
  )
}

function NoPathRows({ items, nameOf }: { items: NoPathStream[]; nameOf: (id?: string) => string }) {
  return (
    <ul className="divide-y divide-line border-t border-line" data-testid="node-no-path">
      {items.map((x) => (
        <li key={x.stream} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm" data-testid={`node-no-path-${x.stream}`}>
          <span className="font-mono text-xs font-semibold">{x.stream}</span>
          <StateBadge spec={ROUTE_SPEC.no_path} />
          <span className="text-xs text-text-soft">
            no relay route: {NO_PATH_TEXT[x.reason] ?? x.reason} · OBS on {nameOf(x.ingress_node_id)} · {ago(x.since)}
          </span>
        </li>
      ))}
    </ul>
  )
}

export function NodeStreams({ n, live, routes }: { n: NodeInfo; live: RelayLive; routes: { items: RelayRoute[]; loading: boolean; error: unknown; refetch: () => unknown; fetching: boolean } }) {
  const names = useNames()
  if (routes.loading) {
    return (
      <NetPanel>
        <SkeletonRows rows={4} cols={6} />
      </NetPanel>
    )
  }
  if (routes.error) return <ErrorBox testId="node-streams-error" text={errorText(`${RELAY}/routes?node=${n.id}`, routes.error)} busy={routes.fetching} onRetry={() => void routes.refetch()} />
  const ingress = routes.items.filter((r) => r.path[0]?.id === n.id)
  const through = routes.items.filter((r) => r.path[0]?.id !== n.id)
  const noPathAll = [...(n.relay?.no_path ?? []), ...[...live.noPath.values()].filter((x) => x.ingress_node_id === n.id)]
  const noPath = [...new Map(noPathAll.map((x) => [x.stream, x])).values()]
  const roles = n.roles ?? []
  return (
    <div className="flex flex-col gap-4" data-testid="node-streams">
      <NetPanel
        pad={false}
        testId="node-streams-ingress"
        title={
          <span className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold">Ingress · OBS connects here</h2>
            <span className="num font-mono text-xs text-muted">{ingress.length + noPath.length}</span>
          </span>
        }
      >
        {ingress.length ? (
          <RouteRows rows={ingress} n={n} label="Ingress streams" testId="node-routes-ingress" />
        ) : (
          !noPath.length && (
            <p className="px-4 py-4 text-sm text-muted" data-testid="node-streams-ingress-empty">
              {roles.includes('ingress') ? 'No relayed stream starts on this node now.' : 'This node has no ingress role: OBS does not connect here.'}
            </p>
          )
        )}
        {noPath.length > 0 && <NoPathRows items={noPath} nameOf={names.nameOf} />}
      </NetPanel>
      <NetPanel
        pad={false}
        testId="node-streams-egress"
        title={
          <span className="flex items-center gap-2">
            <h2 className="text-[15px] font-semibold">Legs that end here · egress and hub</h2>
            <span className="num font-mono text-xs text-muted">{through.length}</span>
          </span>
        }
      >
        {through.length ? (
          <RouteRows rows={through} n={n} label="Egress and hub legs" testId="node-routes-egress" />
        ) : (
          <p className="px-4 py-4 text-sm text-muted" data-testid="node-streams-egress-empty">
            {roles.includes('egress') || roles.includes('hub') ? 'No relayed stream leaves to platforms from this node now.' : 'This node is not an egress or hub: no relay legs end here.'}
          </p>
        )}
      </NetPanel>
      <p className="text-xs text-muted">Streams published straight to this node without the relay are in Streams of the admin.</p>
    </div>
  )
}

/* ── events ──────────────────────────────────────────────────────────────── */

const evKey = (e: RelayJournalEvent) => `${e.t}|${e.kind}|${e.node_id}|${e.peer_node_id ?? ''}|${e.route_id ?? ''}`

function OperatorEventRows({ nodeId }: { nodeId: string }) {
  const ops = useOperatorEvents(nodeId)
  if (!ops.length) return null
  return (
    <div data-testid="node-operator-events">
      {ops.map((e) => (
        <div key={e.id} className="sx-ev" data-kind="operator">
          <span className="tm">{utcClock(e.at)}</span>
          <Icon name="user" size={16} />
          <span>
            <b>{e.name}</b> <span className="d">{e.text}</span>
          </span>
        </div>
      ))}
    </div>
  )
}

export function NodeEvents({ n, live }: { n: NodeInfo; live: RelayLive }) {
  const names = useNames()
  const ops = useOperatorEvents(n.id)
  // peer=true: also the events where this node is the peer (a link the other end reported down);
  // a center before API-UI ignores it and the stream's journal fills those in while it is open
  const path = `${RELAY}/journal?node=${encodeURIComponent(n.id)}&peer=true&limit=100`
  const q = useQuery({
    queryKey: relayKeys.nodeJournal(n.id),
    queryFn: async () => {
      try {
        const r = await getAsOf<RelayList<RelayJournalEvent>>(path)
        noteRelayAsOf(r.asOf, r.at)
        return r
      } catch (e) {
        noteRelayFailure(e)
        throw e
      }
    },
    retry: (k, e) => !(e instanceof ApiError && e.status < 500) && k < 1,
    refetchInterval: live.sse === 'open' ? false : POLL_MS,
  })
  if (q.isPending) {
    return (
      <NetPanel>
        <SkeletonRows rows={6} cols={3} />
      </NetPanel>
    )
  }
  if (q.error && !q.data) {
    return (
      <NetPanel pad={false} testId="node-events" title={<h2 className="text-[15px] font-semibold">Relay events · {n.name}</h2>}>
        <div aria-live="polite">
          <OperatorEventRows nodeId={n.id} />
          <ErrorBox testId="node-events-error" text={errorText(path, q.error)} busy={q.isFetching} onRetry={() => void q.refetch()} />
        </div>
      </NetPanel>
    )
  }
  const mine = (e: RelayJournalEvent) => e.node_id === n.id || e.peer_node_id === n.id
  const seen = new Set<string>()
  const all = [...live.journal.filter(mine), ...(q.data?.data.items ?? [])]
    .filter((e) => {
      const k = evKey(e)
      if (seen.has(k)) return false
      seen.add(k)
      return true
    })
    .sort((a, b) => Date.parse(b.t) - Date.parse(a.t))
  return (
    <NetPanel
      pad={false}
      testId="node-events"
      title={
        <span className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold">Relay events · {n.name}</h2>
          <span className="num font-mono text-xs text-muted">{all.length}</span>
        </span>
      }
      aside={<span className="text-xs text-muted">{live.sse === 'open' ? 'live' : 'polling every 5 s'}</span>}
    >
      <div aria-live="polite">
        <OperatorEventRows nodeId={n.id} />
        {(all.length > 0 || ops.length === 0) && <EventList events={all} nameOf={names.nameOf} self={n.id} empty="No relay events for this node in the retention window." />}
      </div>
    </NetPanel>
  )
}
